import { RefObject, useEffect, useMemo, useRef } from "react";

import {
  getTableStoreByRoute,
  useTableStoreByRoute
} from "@/stores/useTableStore";
import { TableRow as MuiTableRow, TableCell } from "@mui/material";
import MuiTableBody from "@mui/material/TableBody";
import { useVirtualizer } from "@tanstack/react-virtual";

import { IntegrationsRow } from "../../../../Pages/Integrations/types";
import { ROUTES, TableRowData } from "../../../../shared";
import useTableItemDetails from "../../../DetailsSection/hooks/useTableItemDetails";
import { isRowDisabled } from "../../functions";
import TableRow from "../DesktopTable/components/TableRow";

interface ITableBodyProps {
  route: ROUTES;
  scrollHorizontal: boolean;
  hasDetails: boolean;
  tableRowSwitch?: (rowData: IntegrationsRow) => Promise<void>;
  scrollElementRef?: RefObject<HTMLDivElement | null>;
  virtualize?: boolean;
}

const TableBody = ({
  route,
  scrollHorizontal,
  hasDetails,
  tableRowSwitch,
  scrollElementRef,
  virtualize = false
}: ITableBodyProps) => {
  const tableRows = useTableStoreByRoute(route, state => state.data);

  const selectableRows = useTableStoreByRoute(
    route,
    state => state.selectableRows
  );

  const setDetailsData = useTableStoreByRoute(
    route,
    state => state.setDetailsData
  );

  const hasInlineActions = useTableStoreByRoute(
    route,
    state => state.inlineActions?.length > 0
  );

  const { getTableItemDetails } = useTableItemDetails();

  const initialized = useRef(false);
  const didScrollToSelected = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      if (tableRows) {
        if (route) {
          const { selectedTableItem, selectedTableItemUrlKey } =
            getTableStoreByRoute(route).getState().params;

          getTableItemDetails({
            itemId: selectedTableItem,
            ...(route === ROUTES.Integrations && {
              selectedTableItemUrlKey: selectedTableItemUrlKey
            })
          });
        }
      } else {
        setDetailsData(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  const companyLevelsList = useMemo(
    () => tableRows?.map((row: any) => row.level) ?? [],
    [tableRows]
  );

  const virtualizer = useVirtualizer({
    count: tableRows?.length ?? 0,
    getScrollElement: () => scrollElementRef?.current ?? null,
    estimateSize: () => 44,
    overscan: 10,
    enabled: virtualize,
    getItemKey: rowIndex => tableRows?.[rowIndex]?.id ?? rowIndex
  });

  useEffect(() => {
    if (!virtualize || didScrollToSelected.current || !tableRows?.length) {
      return;
    }
    didScrollToSelected.current = true;

    const selectedId =
      getTableStoreByRoute(route).getState().params.selectedTableItem;

    if (!selectedId) return;

    const selectedIndex = tableRows.findIndex(
      (tableRow: TableRowData) => tableRow.id === selectedId
    );

    if (selectedIndex >= 0) {
      virtualizer.scrollToIndex(selectedIndex, { align: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRows, virtualize]);

  if (!tableRows) return null;

  const renderRow = (row: TableRowData, index: number) => (
    <TableRow
      key={row.id}
      route={route}
      row={row}
      scrollHorizontal={scrollHorizontal}
      hasDetails={hasDetails}
      disabled={isRowDisabled(selectableRows, row)}
      index={index}
      companyLevelsList={companyLevelsList}
      tableRowSwitch={tableRowSwitch}
    />
  );

  if (!virtualize) {
    return (
      <MuiTableBody data-testid="table-body">
        {tableRows.map((tableRow, index) => renderRow(tableRow, index))}
      </MuiTableBody>
    );
  }

  const colCount = 1 + (hasInlineActions ? 1 : 0);

  const virtualItems = virtualizer.getVirtualItems();

  const paddingTop = virtualItems.length ? virtualItems[0].start : 0;

  const paddingBottom = virtualItems.length
    ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
    : 0;

  const renderSpacer = (height: number) => (
    <MuiTableRow style={{ height }}>
      <TableCell colSpan={colCount} style={{ padding: 0, border: 0 }} />
    </MuiTableRow>
  );

  return (
    <MuiTableBody data-testid="table-body">
      {paddingTop > 0 && renderSpacer(paddingTop)}
      {virtualItems.map(virtualItem =>
        renderRow(tableRows[virtualItem.index], virtualItem.index)
      )}
      {paddingBottom > 0 && renderSpacer(paddingBottom)}
    </MuiTableBody>
  );
};

export default TableBody;
